import { useState, useRef } from "react";
import * as Popover from "@radix-ui/react-popover";
import { CheckmarkIcon, ChevronDownIcon, SpinnerIcon, SearchIcon } from "@/lib/icons";
import { cn } from "@/lib/utils";

export interface ComboboxOption {
  value: string;
  label: string;
  description?: string;
  /**
   * Shown on the closed trigger instead of `label`. For an option whose row
   * splits over two lines: the trigger has only one, so it needs a form that
   * carries the description's information inline.
   */
  triggerLabel?: string;
  /**
   * Render this option muted on the closed trigger, as an unselected field is.
   * For an option that names a value the field did not have set explicitly, so
   * full contrast would read as a deliberate choice.
   */
  triggerMuted?: boolean;
}

interface ComboboxProps {
  options: ComboboxOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  loading?: boolean;
  /**
   * A single option rendered above the search results, outside the scroll area
   * and never filtered by the search term. For a choice that is not one of the
   * listed candidates but a mode, and so must stay reachable while searching.
   */
  pinnedOption?: ComboboxOption;
}

function OptionRow({
  option,
  selected,
  onSelect,
}: {
  option: ComboboxOption;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
    >
      <CheckmarkIcon
        className={cn(
          "mr-2 h-4 w-4 shrink-0",
          selected ? "opacity-100" : "opacity-0"
        )}
      />
      <div className="text-left">
        <div>{option.label}</div>
        {option.description && (
          <div className="text-xs text-muted-foreground">{option.description}</div>
        )}
      </div>
    </button>
  );
}

export function Combobox({
  options,
  value,
  onChange,
  placeholder = "Valitse...",
  searchPlaceholder = "Hae...",
  disabled = false,
  className,
  loading = false,
  pinnedOption,
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  // The nearest Radix Dialog element, resolved when the panel opens. Portaling
  // the panel into the dialog (instead of document.body) keeps it inside the
  // dialog's scroll-lock and focus scope — otherwise the search input loses
  // focus and the list can't scroll. Falls back to body when not in a dialog.
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedOption =
    pinnedOption && pinnedOption.value === value
      ? pinnedOption
      : options.find((o) => o.value === value);

  const filtered = options.filter(
    (o) =>
      o.label.toLowerCase().includes(search.toLowerCase()) ||
      o.description?.toLowerCase().includes(search.toLowerCase())
  );

  const handleOpenChange = (next: boolean) => {
    if (next) {
      const dialog = triggerRef.current?.closest('[role="dialog"]');
      setContainer(dialog instanceof HTMLElement ? dialog : null);
    } else {
      setSearch("");
    }
    setOpen(next);
  };

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild disabled={disabled}>
        <button
          ref={triggerRef}
          type="button"
          className={cn(
            "flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background",
            "focus:outline-none focus:ring-1 focus:ring-ring",
            "disabled:cursor-not-allowed disabled:opacity-50",
            (!selectedOption || selectedOption.triggerMuted) &&
              "text-muted-foreground",
            className
          )}
        >
          <span className="truncate flex items-center gap-2">
            {loading && <SpinnerIcon className="h-3.5 w-3.5 animate-spin shrink-0" />}
            {selectedOption
              ? selectedOption.triggerLabel ?? selectedOption.label
              : loading
                ? "Ladataan..."
                : placeholder}
          </span>
          <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </Popover.Trigger>

      <Popover.Portal container={container ?? undefined}>
        <Popover.Content
          align="start"
          side="bottom"
          sideOffset={4}
          // Focus the search box on open instead of the panel itself.
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            inputRef.current?.focus();
          }}
          className="z-50 rounded-md border bg-popover shadow-md"
          style={{ width: "var(--radix-popover-trigger-width)" }}
        >
          <div className="flex items-center border-b px-3">
            <SearchIcon className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              // text-base below md: this input is auto-focused on open, and a
              // sub-16px field makes iOS Safari zoom the page out of frame.
              className="flex h-9 w-full bg-transparent py-2 text-base md:text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          {pinnedOption && !loading && (
            <div className="border-b p-1">
              <OptionRow
                option={pinnedOption}
                selected={value === pinnedOption.value}
                onSelect={() => {
                  onChange(pinnedOption.value);
                  handleOpenChange(false);
                }}
              />
            </div>
          )}
          <div className="max-h-32 overflow-y-auto p-1">
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-6">
                <SpinnerIcon className="h-4 w-4 animate-spin text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Ladataan...</p>
              </div>
            ) : filtered.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Ei tuloksia
              </p>
            ) : (
              filtered.map((option) => (
                <OptionRow
                  key={option.value}
                  option={option}
                  selected={value === option.value}
                  onSelect={() => {
                    onChange(option.value === value ? null : option.value);
                    handleOpenChange(false);
                  }}
                />
              ))
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
